import { useState, useEffect, useCallback, memo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import { config } from "../../config";
import { setUser } from "../../redux/actions/user";
import UserService from "../../services/User";
import SessionService from "../../services/Session";
import { errorsToLabels, postFormErrorLabel } from "../../utils/Errors";
import {
  logIssueNetworkRequest,
  logIssueNetworkRequestError,
} from "../../utils/Loggers";
import { displayToastMessage } from "../../utils/Toasts";
import { projectsPath } from "../Router";
import { Mixpanel } from "../../utils/Analytics";
import { ArrowRight, ChevronLeft, Lock, Mail, AlertCircle } from "lucide-react";

const LoginForm = memo(({ onSubmit, onViewChange }: any) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700"
        >
          Password
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-2.5 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center"
      >
        {isSubmitting ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Signing in...
          </span>
        ) : (
          <span className="flex items-center">
            Log In
            <ArrowRight className="ml-2 h-4 w-4" />
          </span>
        )}
      </button>

      <div className="flex flex-col sm:flex-row sm:justify-between gap-4 pt-2">
        <button
          type="button"
          onClick={() => onViewChange("Forgot Password")}
          className="text-red-600 text-sm hover:text-red-700 transition-all duration-300 flex items-center"
        >
          Forgot Password?
        </button>
        <button
          type="button"
          onClick={() => onViewChange("Sign Up")}
          className="text-red-600 text-sm hover:text-red-700 transition-all duration-300 flex items-center"
        >
          Create an account
          <ArrowRight className="ml-1 h-4 w-4" />
        </button>
      </div>
    </form>
  );
});

const SignUpForm = memo(({ onSubmit, onViewChange }: any) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700"
        >
          Password
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="password"
            name="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Must be at least 8 characters
        </p>
      </div>

      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mt-4">
        <p className="text-sm text-gray-600">
          By creating an account, you agree to our{" "}
          <a
            href="https://adinsights.ai"
            target="_blank"
            rel="noreferrer"
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://adinsights.ai"
            target="_blank"
            rel="noreferrer"
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Privacy Policy
          </a>
        </p>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-2.5 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center"
      >
        {isSubmitting ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Creating account...
          </span>
        ) : (
          <span className="flex items-center">
            Create Account
            <ArrowRight className="ml-2 h-4 w-4" />
          </span>
        )}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => onViewChange("Log In")}
          className="text-red-600 text-sm hover:text-red-700 transition-all duration-300 flex items-center justify-center mx-auto"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Already have an account? Log In
        </button>
      </div>
    </form>
  );
});

const ActivateAccountForm = memo(({ onSubmit }: any) => {
  const [formData, setFormData] = useState({
    email: "",
    activationToken: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4 flex items-start">
        <AlertCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Check your email for an activation token. If you don't see it, check
          your spam folder.
        </p>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="activationToken"
          className="block text-sm font-medium text-gray-700"
        >
          Activation Token
        </label>
        <input
          id="activationToken"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
          type="text"
          name="activationToken"
          placeholder="Enter the token from your email"
          value={formData.activationToken}
          onChange={handleChange}
          required
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-2.5 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center"
      >
        {isSubmitting ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Activating...
          </span>
        ) : (
          <span className="flex items-center">
            Activate Account
            <ArrowRight className="ml-2 h-4 w-4" />
          </span>
        )}
      </button>
    </form>
  );
});

const ForgotPasswordForm = memo(({ onSubmit, onViewChange }: any) => {
  const [formData, setFormData] = useState({
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-2">
        <p className="text-sm text-gray-600">
          Enter your email address below, and we'll send you a password reset
          link.
        </p>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-2.5 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center"
      >
        {isSubmitting ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Sending reset link...
          </span>
        ) : (
          <span className="flex items-center">
            Send Reset Link
            <ArrowRight className="ml-2 h-4 w-4" />
          </span>
        )}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => onViewChange("Log In")}
          className="text-red-600 text-sm hover:text-red-700 transition-all duration-300 flex items-center justify-center mx-auto"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to login
        </button>
      </div>
    </form>
  );
});

const ResetPasswordForm = memo(({ onSubmit, onViewChange }: any) => {
  const [formData, setFormData] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
    resetToken: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newState = { ...prev, [name]: value };
      if (name === "newPassword" || name === "confirmPassword") {
        setPasswordsMatch(
          !newState.confirmPassword ||
            newState.newPassword === newState.confirmPassword
        );
      }
      return newState;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setPasswordsMatch(false);
      return;
    }

    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-2 flex items-start">
        <AlertCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Enter the reset token from your email along with your new password.
        </p>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="resetToken"
          className="block text-sm font-medium text-gray-700"
        >
          Reset Token
        </label>
        <input
          id="resetToken"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
          type="text"
          name="resetToken"
          placeholder="Enter token from email"
          value={formData.resetToken}
          onChange={handleChange}
          required
        />
      </div>

      <div className="space-y-1">
        <label
          htmlFor="newPassword"
          className="block text-sm font-medium text-gray-700"
        >
          New Password
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="newPassword"
            className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
            type="password"
            name="newPassword"
            placeholder="••••••••"
            value={formData.newPassword}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-medium text-gray-700"
        >
          Confirm Password
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="confirmPassword"
            className={`w-full pl-10 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all ${
              passwordsMatch ? "border-gray-300" : "border-red-500"
            }`}
            type="password"
            name="confirmPassword"
            placeholder="••••••••"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
        </div>
        {!passwordsMatch && (
          <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !passwordsMatch}
        className="w-full py-2.5 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center"
      >
        {isSubmitting ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Resetting password...
          </span>
        ) : (
          <span className="flex items-center">
            Reset Password
            <ArrowRight className="ml-2 h-4 w-4" />
          </span>
        )}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => onViewChange("Log In")}
          className="text-red-600 text-sm hover:text-red-700 transition-all duration-300 flex items-center justify-center mx-auto"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to login
        </button>
      </div>
    </form>
  );
});

const Auth = () => {
  const [currentView, setCurrentView] = useState("Log In");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userService = new UserService();
  const sessionService = new SessionService();

  const handleInitialLoad = useCallback(async () => {
    try {
      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.email && parsedQuery.view === "activate") {
        displayToastMessage(
          "success",
          "Success :D Check your email to activate your account AND MAKE SURE TO RESET YOUR TEMPORARY PASSWORD."
        );
        setCurrentView("Activate Account");
        return;
      }

      const sessionData = await sessionService.reviewSession();
      if (sessionData.success) {
        dispatch(setUser(sessionData.userSessionData));
        navigate(projectsPath, {
          replace: true,
        });
      }
    } catch (error: any) {
      logIssueNetworkRequestError("Auth.handleInitialLoad", error);
    }
  }, [dispatch, location, navigate, sessionService]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const handleLogIn = useCallback(
    async (formData: any) => {
      try {
        logIssueNetworkRequest("handleLogIn");
        displayToastMessage("info", "Loading...");

        const resBody = await sessionService.logIn(formData);
        if (resBody.error) {
          if (resBody.error === errorsToLabels.userMustActivateAccount) {
            displayToastMessage(
              "success",
              "Please check your email for an activation token"
            );
            setCurrentView("Activate Account");
            return;
          }
          throw new Error(resBody.error);
        }

        displayToastMessage("success", "Success");

        if (resBody.sessionURL) {
          // New subscription - redirect to Stripe Checkout
          window.location.href = resBody.sessionURL;
          return;
        }

        Mixpanel.identify(resBody.user.id);
        Mixpanel.track("Log In (start)", {
          email: formData.email,
        });

        dispatch(setUser(resBody.user));
        navigate(projectsPath, { replace: true, state: { session: true } });
      } catch (error: any) {
        logIssueNetworkRequestError("handleLogIn", error);
        displayToastMessage("error", postFormErrorLabel("Log In"));
      }
    },
    [dispatch, navigate, sessionService]
  );

  const handleSignUp = useCallback(
    async (formData: any) => {
      try {
        logIssueNetworkRequest("handleSignUp");
        displayToastMessage("info", "Loading...");

        const resBody = await userService.signUp(formData);
        if (resBody.error) {
          throw new Error(resBody.error);
        }
        if (resBody.accountLink) {
          window.open(resBody.accountLink.url, "_blank")?.focus();
        }

        Mixpanel.track("Sign Up", {
          email: formData.email,
        });

        displayToastMessage(
          "success",
          "Please check your email for an activation token"
        );
        setCurrentView("Activate Account");
      } catch (error: any) {
        logIssueNetworkRequestError("handleSignUp", error);
        displayToastMessage("error", postFormErrorLabel("Sign Up"));
      }
    },
    [userService]
  );

  const handleActivateAccount = useCallback(
    async (formData: any) => {
      try {
        logIssueNetworkRequest("handleActivateAccount");
        displayToastMessage("info", "Loading...");

        Mixpanel.track("Activate Account", {
          email: formData.email,
        });

        const resBody = await userService.activateAccount(formData);
        if (resBody.error) {
          throw new Error(resBody.error);
        }

        displayToastMessage("success", "Please log in again :)");
        setCurrentView("Log In");
      } catch (error: any) {
        logIssueNetworkRequestError("handleActivateAccount", error);
        displayToastMessage("error", postFormErrorLabel("Account Activation"));
      }
    },
    [userService]
  );

  const handleForgotPassword = useCallback(
    async (formData: any) => {
      try {
        logIssueNetworkRequest("handleForgotPassword");
        displayToastMessage("info", "Loading...");

        const resBody = await userService.forgotPassword(formData);
        if (resBody.error) {
          throw new Error(resBody.error);
        }

        displayToastMessage(
          "success",
          "Please check your email for your reset token"
        );
        setCurrentView("Reset Password");
      } catch (error: any) {
        logIssueNetworkRequestError("handleForgotPassword", error);
        displayToastMessage("error", postFormErrorLabel("Forgot Password"));
      }
    },
    [userService]
  );

  const handleResetPassword = useCallback(
    async (formData: any) => {
      try {
        logIssueNetworkRequest("handleResetPassword");
        displayToastMessage("info", "Loading...");

        const resBody = await userService.resetPassword(formData);
        if (resBody.error) {
          throw new Error(resBody.error);
        }

        displayToastMessage("success", "Please log in again :)");
        setCurrentView("Log In");
      } catch (error: any) {
        logIssueNetworkRequestError("handleResetPassword", error);
        displayToastMessage("error", postFormErrorLabel("Reset Password"));
      }
    },
    [userService]
  );

  const renderAuthForm = () => {
    switch (currentView) {
      case "Sign Up":
        return (
          <SignUpForm onSubmit={handleSignUp} onViewChange={setCurrentView} />
        );
      case "Activate Account":
        return <ActivateAccountForm onSubmit={handleActivateAccount} />;
      case "Forgot Password":
        return (
          <ForgotPasswordForm
            onSubmit={handleForgotPassword}
            onViewChange={setCurrentView}
          />
        );
      case "Reset Password":
        return (
          <ResetPasswordForm
            onSubmit={handleResetPassword}
            onViewChange={setCurrentView}
          />
        );
      case "Log In":
      default:
        return (
          <LoginForm onSubmit={handleLogIn} onViewChange={setCurrentView} />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg border border-gray-200">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            {config.labels.brandName}
          </h2>
          <h3 className="mt-2 text-center text-lg text-gray-600">
            {currentView}
          </h3>
        </div>
        {renderAuthForm()}
      </div>
    </div>
  );
};

export default Auth;
